import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useParallax } from '../../../hooks/useParallax';

export default function ServicesSection() {
  const { ref: sectionRef, isVisible } = useScrollAnimation<HTMLElement>();
  const { ref: decorRef, style: decorStyle } = useParallax<HTMLDivElement>({ speed: 0.15, direction: 'down' });

  const services = [
    {
      icon: 'ri-user-star-line',
      title: 'Пакет «СТРАТЕГ» — HR-директор на аутсорсе',
      desc: 'Ваш персональный стратег по персоналу на постоянной основе.',
      items: [
        'Полное управление HR-функцией: от разработки стратегии до ежедневной операционной работы.',
        'Внедрение систем KPI и мотивации, привязанных к бизнес-целям.',
        'Полная юридическая защита и легализация кадровых процессов.',
        'Разрешение сложных конфликтов, работа с командой и климатом.',
      ],
      price: 'Стоимость: от 90 000 руб./мес.',
    },
    {
      icon: 'ri-rocket-line',
      title: 'Пакет «ЗАПУСК» — Создание HR-службы с нуля «под ключ»',
      desc: 'Для компаний, которые выросли и нуждаются в структуре.',
      items: [
        'Глубокий аудит существующих процессов и рисков.',
        'Разработка полного пакета локальных нормативных актов (ПВТР, Положения об оплате труда, о подборе и т.д.).',
        'Подбор, обучение и введение в должность вашего будущего HR-специалиста/менеджера.',
      ],
      price: 'Стоимость: от 150 000 руб.',
    },
    {
      icon: 'ri-search-eye-line',
      title: 'Пакет «ПОДБОР ПОД КЛЮЧ» — Закрываем вакансии системно',
      desc: '',
      items: [
        'Массовый подбор (конвейер): для рабочих, вахтовиков, линейного персонала. Стоимость за найм: от 15 000 руб./чел. или фиксированная за проект.',
        'Executive Search: целевой поиск и оценка руководителей и ключевых специалистов. 30% от годового дохода кандидата или фиксированная от 120 000 руб. за позицию.',
        'Поддержка соискателей (HR-сервис): помощь с резюме, подготовка к собеседованию. 5 000 руб./чел.',
      ],
      price: '',
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Пакет «ОПТИМИЗАЦИЯ» — Повышаем эффективность и снижаем издержки',
      desc: '',
      items: [
        'Аудит и оптимизация фонда оплаты труда (ФОТ).',
        'Реструктуризация подразделений для повышения управляемости.',
        'Подбор и внедрение IT-систем (ATS, HRM, документооборот).',
      ],
      price: 'Стоимость: от 70 000 руб.',
    },
  ];

  return (
    <section 
      id="services" 
      ref={sectionRef}
      className="bg-dark-light py-16 sm:py-24 px-4 sm:px-8 lg:px-16 relative overflow-hidden"
    >
      {/* Parallax Decorative Elements */}
      <div ref={decorRef} style={decorStyle} className="absolute inset-0 pointer-events-none">
        <div className="absolute top-40 -right-32 w-72 h-72 bg-gold/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 -left-32 w-64 h-64 bg-gold/3 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        <div 
          className={`mb-10 sm:mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="w-16 h-0.5 bg-gold mb-6 sm:mb-8"></div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
            <a href="#services"><strong>Наши услуги и пакеты</strong></a>
          </h2>
        </div>

        <div className="space-y-0">
          {services.map((s, i) => (
            <div
              key={i}
              className={`border-t border-white/10 py-8 sm:py-10 group hover:bg-white/[0.02] hover:pl-4 transition-all duration-500 ease-out rounded-lg ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: isVisible ? `${(i + 1) * 100}ms` : '0ms' }}
            >
              <div className="flex items-start gap-4 sm:gap-6">
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className={`${s.icon} text-xl text-gold`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-heading text-lg sm:text-2xl font-bold text-white mb-2 group-hover:text-gold transition-colors duration-300">
                    <a href="#services">{s.title}</a>
                  </h4>
                  {s.desc && (
                    <p className="font-body text-sm text-white/50 mb-4">{s.desc}</p>
                  )}
                  <ul className="space-y-2 mb-4">
                    {s.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <span className="text-gold mt-1.5 text-xs flex-shrink-0">&#9679;</span>
                        <span className="font-body text-xs sm:text-sm text-white/70 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                  {s.price && (
                    <p className="font-body text-sm font-semibold text-gold">{s.price}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Услуги по запросу */}
        <div 
          className={`border-t border-white/10 pt-8 sm:pt-10 mt-2 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: isVisible ? '600ms' : '0ms' }}
        >
          <h3 className="font-heading text-xl sm:text-2xl font-bold text-white mb-6 sm:mb-8">
            <a href="#services">Услуги по запросу</a>
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-dark/60 border border-white/10 rounded-lg p-6 sm:p-8 hover:-translate-y-2 hover:border-gold/40 hover:shadow-2xl hover:shadow-gold/10 transition-all duration-500 ease-out">
              <div className="w-10 h-10 flex items-center justify-center mb-4">
                <i className="ri-mental-health-line text-xl text-gold"></i>
              </div>
              <h4 className="font-heading text-lg font-semibold text-white mb-4">
                <a href="#services">Коучинг и психологическая поддержка</a>
              </h4>
              <ul className="space-y-3">
                <li className="font-body text-xs sm:text-sm text-white/60">
                  <span className="text-white/80">Консультация для собственников/топ-менеджеров</span> по управлению командой — <span className="text-gold font-semibold">12 000 руб./сессия</span> (2 часа)
                </li>
                <li className="font-body text-xs sm:text-sm text-white/60">
                  <span className="text-white/80">Коучинг ключевых сотрудников</span> на повышение лояльности и эффективности — <span className="text-gold font-semibold">8 000 руб./сессия</span>
                </li>
              </ul>
            </div>
            <div className="space-y-6">
              <div className="bg-dark/60 border border-white/10 rounded-lg p-6 sm:p-8 hover:-translate-y-2 hover:border-gold/40 hover:shadow-2xl hover:shadow-gold/10 transition-all duration-500 ease-out">
                <h4 className="font-heading text-lg font-semibold text-white mb-2">
                  <a href="#services">Комплексный HR-аудит</a>
                </h4>
                <p className="font-body text-xs sm:text-sm text-white/60 mb-2">Проверка всех процессов на эффективность и юридические риски с подробным отчётом и roadmap.</p>
                <p className="font-body text-sm text-gold font-semibold">от 50 000 руб.</p>
              </div>
              <div className="bg-dark/60 border border-white/10 rounded-lg p-6 sm:p-8 hover:-translate-y-2 hover:border-gold/40 hover:shadow-2xl hover:shadow-gold/10 transition-all duration-500 ease-out">
                <h4 className="font-heading text-lg font-semibold text-white mb-2">
                  <a href="#services">Разовая экспертная консультация</a>
                </h4>
                <p className="font-body text-xs sm:text-sm text-white/60 mb-2">По конкретному вопросу: увольнение, конфликт, мотивация.</p>
                <p className="font-body text-sm text-gold font-semibold">7 000 руб./1,5 часа</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
